import { useEffect, useRef } from "react";
import { onPolicyEvent } from "../../../../Service/realtime";
import { BATCH_MS, RELOAD_GAP_MS, planListUpdate, rowPatchFromPolicy } from "../utils/livePolicyList";

/**
 * Keeps the open Online Policy list up to date from live policy updates (2026-10-22). No polling: it only acts when the
 * server says a policy changed, and then as little as possible (see utils/livePolicyList.js).
 *
 * `latest` is read through a ref on every message, so the reload always uses the page, search, filters and sort that
 * are on screen at that moment - never the ones from when the page opened.
 *
 * @param {Object} latest  { enabled, tableData, page, reload(), fetchPolicyByIdSilent(id), patchTableRow(id, patch) }
 */
export const useLivePolicyList = (latest, { now = Date.now } = {}) => {
  const ref = useRef(latest);
  ref.current = latest;

  useEffect(() => {
    let batch = { ids: [], resync: false, reloadIds: [] };
    let batchTimer = null;
    let reloadTimer = null;
    let lastReload = 0;

    const reload = () => {
      if (reloadTimer) return; // one is already on its way
      const wait = Math.max(0, lastReload + RELOAD_GAP_MS - now());
      reloadTimer = setTimeout(() => {
        reloadTimer = null;
        lastReload = now();
        if (ref.current.enabled) ref.current.reload();
      }, wait);
    };

    const flush = async () => {
      batchTimer = null;
      const current = batch;
      batch = { ids: [], resync: false, reloadIds: [] };
      const l = ref.current;
      if (!l.enabled) return;
      const plan = planListUpdate(current, l.tableData, l.page);
      if (plan.reload) return reload();
      for (const id of plan.patch) {
        const fresh = await ref.current.fetchPolicyByIdSilent(id);
        const patch = rowPatchFromPolicy(fresh);
        if (patch) ref.current.patchTableRow(id, patch);
      }
    };

    const off = onPolicyEvent((e) => {
      if (e.type === "resync") batch.resync = true;
      else if (e.type === "changed" && e.policyId) {
        batch.ids.push(String(e.policyId));
        // the policy's own data was edited (or it was deleted): a status patch is not enough
        if (e.kind === "edited" || e.kind === "deleted") batch.reloadIds.push(String(e.policyId));
      }
      else return;
      if (!batchTimer) batchTimer = setTimeout(flush, BATCH_MS);
    });

    return () => {
      off();
      if (batchTimer) clearTimeout(batchTimer);
      if (reloadTimer) clearTimeout(reloadTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
